import { closestCalc } from "#src/util/helpers.js";
import { MODES } from "#src/constants/mode.ts";
import { type Product, type ProductTypeInfo } from "#src/types/Product.ts";
import { PRODUCTS } from "#src/constants/products.ts";
import {
  type QuoteParams,
  type QuoteRequestParams,
  type BenefitPeriodDataItem,
  type InflationDataItem,
  type PartnerDiscountDataItem,
  type PayDurationDataItem,
  type ReturnOfPremiumDataItem,
  type SolveDataItem,
  type RenderRules,
  type ModeDataItem,
  type InflationOption,
  SOLVE,
  PARTNER_DISCOUNT,
  MONTHLY_BENEFITS,
  MONTHLY_BENEFIT_MOST_COMMON,
  PARTNER_DISCOUNT_REQUIREMENTS,
  getInflationDataItem,
  insuredQuoteData,
  INFLATION_OPTION,
  RETURN_OF_PREMIUM_OPTIONS,
} from "#src/types/QuoteParams.ts";

export const BENEFIT_PERIOD_OPTIONS: BenefitPeriodDataItem[] = (() => {
  const options = [];
  for (let i = 2; i <= 8; i++) {
    options.push({ value: i, text: `${i} Years` });
  }

  options.push({ value: -1, text: "Unlimited" });
  return options;
})();

export interface LinkedBenefitProductTypeInfo extends ProductTypeInfo {
  isLinkedBenefit: boolean;
  solves: SolveDataItem[];
  partnerDiscountOptions: PartnerDiscountDataItem[];
  returnOfPremiumOptions: ReturnOfPremiumDataItem[];
  payDurationOptions: PayDurationDataItem[];
  inflationOptions: InflationDataItem[];
  benefitPeriodOptions: BenefitPeriodDataItem[];
}

function showMonthlyLtcBenefit(params: QuoteParams) {
  return params.solve === SOLVE.BASE_PREMIUM;
}

function showPremium(params: QuoteParams) {
  return params.solve === SOLVE.LTC_BENEFIT;
}

function showHealth(conversion: boolean) {
  return !conversion;
}
export default function LinkedBenefit(): LinkedBenefitProductTypeInfo {
  return {
    get type(): Product {
      return PRODUCTS.LINKED_BENEFIT;
    },
    get isLinkedBenefit(): boolean {
      return true;
    },
    get display(): string {
      return "Linked Benefit";
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    get defaults(): Partial<QuoteRequestParams> {
      return {
        solve: SOLVE.BASE_PREMIUM,
        categories: [this.requestCategory],
        monthly_benefit: MONTHLY_BENEFIT_MOST_COMMON,
        inflation_percentage: null,
        pay_duration: -1,
        benefit_period: 6,
        return_of_premium_rider: null,
        partner_discount: null,
        save_age: false,
      };
    },
    get requestCategory(): string {
      return "universal_life.linked_benefit";
    },
    get solves(): SolveDataItem[] {
      return [
        { value: SOLVE.BASE_PREMIUM, text: "Premium" },
        { value: SOLVE.LTC_BENEFIT, text: "LTC Benefit" },
      ];
    },
    get partnerDiscountOptions(): PartnerDiscountDataItem[] {
      return [
        {
          text: "Single",
          value: PARTNER_DISCOUNT.SINGLE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
        {
          text: "Married, Applying with Spouse",
          value: PARTNER_DISCOUNT.JOINT,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.VERBOSE,
        },
        {
          text: "Married, Applying Individually",
          value: PARTNER_DISCOUNT.SPOUSE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
      ];
    },
    get returnOfPremiumOptions(): ReturnOfPremiumDataItem[] {
      return RETURN_OF_PREMIUM_OPTIONS;
    },
    get payDurationOptions(): PayDurationDataItem[] {
      return [
        { value: 1, title: "Single Pay" },
        { value: 5, title: "5 Pay" },
        { value: 10, title: "10 Pay" },
        { value: -65, title: "Pay to 65" },
        { value: -1, title: "Lifetime Pay" },
      ];
    },
    get inflationOptions(): InflationDataItem[] {
      const items: InflationOption[] = [
        INFLATION_OPTION.NONE,
        INFLATION_OPTION.SIMPLE_3,
        INFLATION_OPTION.COMPOUND_3,
        INFLATION_OPTION.COMPOUND_5,
      ];

      return items.map(getInflationDataItem);
    },
    get benefitPeriodOptions(): BenefitPeriodDataItem[] {
      return BENEFIT_PERIOD_OPTIONS;
    },
    eAppParser(model): Partial<QuoteParams> {
      let solve = model?.solve;
      if (!this.solves.some(v => v.value === solve)) {
        solve = this.defaults.solve;
      }

      const monthly_benefit = closestCalc(
        model.monthly_benefit || this.defaults.monthly_benefit,
        MONTHLY_BENEFITS
      );

      let inflation_percentage = model?.inflation_percentage;
      if (!this.inflationOptions.some(v => v.value === inflation_percentage)) {
        inflation_percentage = this.defaults.inflation_percentage;
      }

      let pay_duration = model?.pay_duration;
      if (!this.payDurationOptions.some(v => pay_duration === v.value)) {
        pay_duration = this.defaults.pay_duration;
      }

      let benefit_period = this.defaults.benefit_period;
      if (model?.benefit_period) {
        benefit_period = closestCalc(
          model.benefit_period,
          BENEFIT_PERIOD_OPTIONS.map(v => v.value)
        );
      }

      let return_of_premium_rider = model?.return_of_premium_rider;
      if (
        !this.returnOfPremiumOptions.some(
          v => v.value === return_of_premium_rider
        )
      ) {
        return_of_premium_rider = this.defaults.return_of_premium_rider;
      }

      let partner_discount = model?.partner_discount;
      if (
        !this.partnerDiscountOptions.some(v => v.value === partner_discount)
      ) {
        partner_discount = this.defaults.partner_discount;
      }

      let saveAge = model.save_age;
      if (!(typeof saveAge === "boolean")) saveAge = this.defaults.save_age;

      return {
        solve,
        monthly_benefit,
        inflation_percentage,
        pay_duration,
        benefit_period,
        return_of_premium_rider,
        partner_discount,
        save_age: saveAge,
      };
    },
    renderRules(
      params = {},
      options = { conversion: false }
    ): Partial<RenderRules> {
      const shouldShowHealth = showHealth(options.conversion);
      const jointInsuredHealth =
        params.partner_discount === PARTNER_DISCOUNT.JOINT && shouldShowHealth;

      return {
        saveAge: true,
        premium: showPremium(params),
        monthlyBenefit: showMonthlyLtcBenefit(params),
        inflationPercentage: true,
        benefitPeriod: true,
        payDuration: true,
        solve: true,
        partnerDiscount: true,
        returnOfPremium: true,
        insuredHealth: shouldShowHealth,
        jointInsuredHealth: jointInsuredHealth,
        mode: true,
        state: true,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      const body: Partial<QuoteRequestParams> = {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        mode: args.params.mode,
        categories: this.defaults.categories,

        save_age: args.params.save_age,
        solve: args.params.solve,
        inflation_percentage: args.params.inflation_percentage,
        benefit_period: args.params.benefit_period,
        pay_duration: args.params.pay_duration,
        return_of_premium_rider: args.params.return_of_premium_rider,
        partner_discount: args.params.partner_discount,
      };

      if (showMonthlyLtcBenefit(args.params)) {
        body.monthly_benefit = args.params.monthly_benefit;
      } else if (showPremium(args.params)) {
        body.premium = args.params.premium;
      }

      if (
        args.params.partner_discount === PARTNER_DISCOUNT.JOINT &&
        args.otherInsured
      ) {
        body.joint_party = insuredQuoteData(args.otherInsured);
      }

      return body;
    },
  };
}
